/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { useEffect, useRef, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { createPortal } from 'react-dom'
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa'
import { IoClose } from 'react-icons/io5'
import { useIntl } from 'react-intl'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { useAuth } from '../../../../app/modules/auth'
import { StepperComponent } from '../../../assets/ts/components'
import { defaultCreateAppData, ICreateAppData } from './IAppModels'
import { Step1 } from './steps/Step1'
import { Step2 } from './steps/Step2'

type Props = {
  show: boolean
  handleClose: () => void
  claimDetails: any
  formattedDate: string
  onSuccessSubmit: () => void
  monitoringDetailsByYesterday?: any
}

const modalsRoot = document.getElementById('root-modals') || document.body

const CreateAppModal = ({
  show,
  handleClose,
  claimDetails,
  formattedDate,
  onSuccessSubmit,
  monitoringDetailsByYesterday,
}: Props) => {
  const user = useAuth()
  const {auth} = useAuth()
  const {formatMessage: f} = useIntl()
  const stepperRef = useRef<HTMLDivElement | null>(null)
  const stepper = useRef<StepperComponent | null>(null)
  const [data, setData] = useState<ICreateAppData>(defaultCreateAppData)
  const [hasError, setHasError] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [curStep, setCurStep] = useState(0)
  const [isStep1Valid, setIsStep1Valid] = useState(false)
  const [isStep2Valid, setIsStep2Valid] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)

  console.log('CLAIM DETAILS', claimDetails)

  // Pre-populate form with yesterday's monitoring data if available
  useEffect(() => {
    if (show) {
      try {
        // Parse claimData JSON string to extract IcdCode1
        const claimData =
          typeof claimDetails?.claimDetails?.claim?.claimData === 'string'
            ? JSON.parse(claimDetails.claimDetails.claim.claimData)
            : claimDetails?.claimDetails?.claim?.claimData

        const icd10FromClaim = claimData?.IcdCode1 || ''

        console.log('Pre-filling ICD10 from claimData:', icd10FromClaim)

        setData((prevData) => ({
          ...prevData,
          appBasic: {
            ...prevData.appBasic,
            icd10: icd10FromClaim ? icd10FromClaim : prevData.appBasic.icd10,
          },
        }))
      } catch (error) {
        console.error('Failed to parse claimData for ICD10 pre-fill:', error)
      }
    }
  }, [show])

  // Reset state when modal is closed
  useEffect(() => {
    if (!show) {
      setData(defaultCreateAppData)
      setHasError(false)
      setCurrentStep(0)
      setCurStep(0)
      setIsStep1Valid(false)
      setIsStep2Valid(false)
    }
  }, [show])

  // Initialize stepper when modal is shown
  useEffect(() => {
    if (show && stepperRef.current) {
      stepper.current = StepperComponent.createInsance(stepperRef.current)
    }
  }, [show])

  const formatDate = () => {
    return formattedDate
  }

  const loadStepper = () => {
    if (stepperRef.current) {
      stepper.current = StepperComponent.createInsance(stepperRef.current)
    } else {
      console.error('stepperRef.current is null')
    }
  }

  const updateData = (fieldsToUpdate: Partial<ICreateAppData>) => {
    const updatedData = {...data, ...fieldsToUpdate}
    setData(updatedData)
  }

  const prevStep = () => {
    if (!stepper.current) {
      return
    }
    stepper.current.goPrev()
    setCurrentStep(stepper.current?.currentStepIndex || 0)
  }

  const nextStep = () => {
    setHasError(false)
    if (!stepper.current) {
      return
    }

    stepper.current.goNext()
    setCurrentStep(stepper.current?.currentStepIndex || 0)
  }

  const handleSubmitClick = () => {
    setShowConfirmation(true)
  }

  const submit = async () => {
    setLoading(true)
    try {
      const claimNumber = claimDetails.claimDetails.claim.claimNumber

      // Create the monitoring data
      const monitoringResponse = await fetch(
        `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/DailyMonitoring`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
          },
          body: JSON.stringify({
            claimNumber: claimNumber,
            monitoringDate: formattedDate,
            icdCode1: data.appBasic.icd10?.split(' - ')[0] || '',
            icdCode2: data.appBasic.icd102?.split(' - ')[0] || '',
            icdCode3: data.appBasic.icd103?.split(' - ')[0] || '',
            icdCode4: data.appBasic.icd104?.split(' - ')[0] || '',
            laboratory: data.appBasic.laboratorium,
            generalCondition: data.appBasic.generalCondition,
            doctor: data.appBasic.doctor,
            specialist: data.appBasic.specialist,
            medicalExpense: data.appBasic.medicalExpense,
            upgradeRoom: data.appBasic.upgradeRoom,
            reason: data.appBasic.reason,
            isAps: data.appBasic.isAPS,
            costSaving: data.appBasic.costSaving,
            remarkCostSaving: data.appBasic.remarksCostSaving,
            pulse: data.appBasic.pulse,
            respiratory: data.appBasic.respiratory,
            bloodPressure: data.appBasic.bloodPressure,
            temperature: data.appBasic.temperature,
            chiefComplain: null,
            vas: null,
            iv: null,
            oral: null,
            supporting: data.appBasic.supporting,
            followUp: data.appBasic.followUp,
            treatment: data.appBasic.treatment,
            remark: data.appBasic.remarks,
            radiology: null,
            medicine: null,
            focusOfManagement: null,
            username: user.currentUser?.email,
          }),
        }
      )

      if (!monitoringResponse.ok) {
        throw new Error('Failed to create daily monitoring')
      }

      const monitoringData = await monitoringResponse.json()
      const idMonitoring = monitoringData.id

      // Handle file uploads if there are any
      if (data.appFiles?.files?.length > 0) {
        // Check file size limit (10MB)
        const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB in bytes
        const oversizedFiles = data.appFiles.files.filter((file: File) => file.size > MAX_FILE_SIZE)

        if (oversizedFiles.length > 0) {
          const fileNames = oversizedFiles.map((f: File) => f.name).join(', ')
          toast.error(`File size exceeds maximum allowed size of 10MB: ${fileNames}`)
          setLoading(false)
          return
        }

        const formData = new FormData()

        data.appFiles.files.forEach((file: File) => {
          formData.append('files', file)
        })

        const uploadUrl = `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/DailyMonitoringUpload?idMonitoring=${idMonitoring}&claimNumber=${claimNumber}&user=${user.currentUser?.email}`

        const uploadResponse = await fetch(uploadUrl, {
          method: 'POST',
          body: formData,
          headers: {
            Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
          },
        })

        if (!uploadResponse.ok) {
          throw new Error('Failed to upload files')
        }
      }

      // Only call UpdateStatus API if status is not Daily Monitoring
      if (claimDetails.claimDetails.claim.status !== 'Daily Monitoring') {
        const statusChangeResponse = await fetch(
          `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/UpdateStatus`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
            },
            body: JSON.stringify({
              claimNumber: claimNumber,
              status: 'Daily Monitoring',
              username: user.currentUser?.email,
              source: 'Provider',
            }),
          }
        )

        if (!statusChangeResponse) {
          console.warn('Failed to update status to Daily Monitoring')
        }
      }

      await toast.success('Submission and Upload Successful!', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      })

      onSuccessSubmit()
      handleClose()
      setShowConfirmation(false)

      setTimeout(() => {
        window.location.reload()
      }, 500)
    } catch (error) {
      console.error('Error:', error)
      toast.error('Error: ' + (error as Error).message)
      setShowConfirmation(false)
    } finally {
      setLoading(false)
    }
  }

  const handleValidationChange = (isValid: boolean) => {
    setIsStep1Valid(isValid)
  }

  const handleValidationChange2 = (isValid: boolean) => {
    setIsStep2Valid(isValid)
  }

  useEffect(() => {
    if (!show) {
      setData(defaultCreateAppData)
      setHasError(false)
      setCurrentStep(0)
      setCurStep(0)
    }
  }, [show])

  return createPortal(
    <>
      {/* Main modal - opacity controlled by showConfirmation state */}
      <Modal
        data-testid='create-app-modal'
        id='kt_modal_create_app'
        tabIndex={-1}
        aria-hidden='true'
        dialogClassName='modal-dialog modal-dialog-centered mw-900px'
        show={show}
        onHide={handleClose}
        onEntered={loadStepper}
        backdrop={true}
        style={{
          opacity: showConfirmation ? 0 : 1,
          transition: 'opacity 0.2s ease-in-out',
        }}
      >
        {/* Main modal content */}
        <div
          className='modal-header'
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
            <span style={{color: 'gray', fontSize: '16px'}}>{formatDate()} </span>
            <h2 style={{margin: 0}}>{f({id: 'CLAIM.INPATIENT.CREATE_MONITORING'})} </h2>
          </div>
          <div
            className='btn btn-sm btn-icon btn-active-color-primary'
            onClick={handleClose}
            style={{
              marginLeft: 'auto',
              cursor: 'pointer',
            }}
          >
            <IoClose className='fs-1' />
          </div>
        </div>

        <div className='modal-body py-lg-10 px-lg-10'>
          {/*begin::Stepper */}
          <div
            ref={stepperRef}
            className='stepper stepper-pills stepper-column d-flex flex-column flex-xl-row flex-row-fluid'
            id='kt_modal_create_app_stepper'
          >
            {/* begin::Aside*/}
            <div className='d-flex justify-content-center justify-content-xl-start flex-row-auto w-100 w-xl-300px'>
              {/* begin::Nav*/}
              <div className='stepper-nav ps-lg-10'>
                {/* begin::Step 1*/}
                <div className='stepper-item current' data-kt-stepper-element='nav'>
                  {/* begin::Wrapper*/}
                  <div className='stepper-wrapper'>
                    {/* begin::Icon*/}
                    <div className='stepper-icon w-40px h-40px'>
                      <i className='stepper-check fas fa-check'></i>
                      <span className='stepper-number'>1</span>
                    </div>
                    {/* end::Icon*/}

                    {/* begin::Label*/}
                    <div className='stepper-label'>
                      <h3 className='stepper-title'>{f({id: 'CLAIM.INPATIENT.MANDATORY'})} </h3>
                    </div>
                    {/* end::Label*/}
                  </div>
                  {/* end::Wrapper*/}

                  {/* begin::Line*/}
                  <div className='stepper-line h-40px'></div>
                  {/* end::Line*/}
                </div>
                {/* end::Step 1*/}

                {/* begin::Step 2*/}
                <div className='stepper-item' data-kt-stepper-element='nav'>
                  {/* begin::Wrapper*/}
                  <div className='stepper-wrapper'>
                    {/* begin::Icon*/}
                    <div className='stepper-icon w-40px h-40px'>
                      <i className='stepper-check fas fa-check'></i>
                      <span className='stepper-number'>2</span>
                    </div>
                    {/* begin::Icon*/}

                    {/* begin::Label*/}
                    <div className='stepper-label'>
                      <h3 className='stepper-title'>{f({id: 'CLAIM.INPATIENT.OPTIONAL'})} </h3>
                    </div>
                    {/* begin::Label*/}
                  </div>
                  {/* end::Wrapper*/}

                  {/* begin::Line*/}
                  <div className='stepper-line h-40px'></div>
                  {/* end::Line*/}
                </div>
                {/* end::Step 2*/}
              </div>
              {/* end::Nav*/}
            </div>
            {/* begin::Aside*/}

            {/*begin::Content */}
            <div className='flex-row-fluid py-lg-5 px-lg-15'>
              {/*begin::Form */}
              <form noValidate id='kt_modal_create_app_form'>
                <Step1
                  data={data}
                  updateData={updateData}
                  hasError={hasError}
                  onValidationChange={handleValidationChange}
                  claimDetails={claimDetails}
                />
                <Step2
                  data={data}
                  updateData={updateData}
                  hasError={hasError}
                  onValidationChange={handleValidationChange2}
                />

                {/*begin::Actions */}
                <div className='d-flex flex-stack pt-10'>
                  <div className='me-2'>
                    <button
                      type='button'
                      className='btn btn-lg btn-light-primary me-3'
                      data-kt-stepper-action='previous'
                      onClick={prevStep}
                    >
                      <FaArrowLeft className='me-1' /> {f({id: 'CLAIM.INPATIENT.PREVIOUS'})}
                    </button>
                  </div>
                  <div>
                    <button
                      type='button'
                      className='btn btn-lg btn-primary'
                      data-kt-stepper-action='submit'
                      onClick={handleSubmitClick}
                      disabled={loading}
                    >
                      {loading ? (
                        <span>
                          <span
                            className='spinner-border spinner-border-sm'
                            role='status'
                            aria-hidden='true'
                          ></span>{' '}
                          {f({id: 'CLAIM.INPATIENT.SUBMITTING'})}
                        </span>
                      ) : (
                        <>
                          {f({id: 'CLAIM.INPATIENT.SUBMIT'})} <FaArrowRight className='ms-2 me-0' />
                        </>
                      )}
                    </button>

                    {currentStep === 0 && (
                      <button
                        type='button'
                        className='btn btn-lg btn-primary'
                        data-kt-stepper-action='next'
                        onClick={nextStep}
                        disabled={!isStep1Valid}
                      >
                        {f({id: 'CLAIM.INPATIENT.NEXT'})} <FaArrowRight className='ms-1 me-0' />
                      </button>
                    )}

                    {currentStep === 1 && (
                      <button
                        type='button'
                        className='btn btn-lg btn-primary'
                        data-kt-stepper-action='next'
                        onClick={nextStep}
                      >
                        {f({id: 'CLAIM.INPATIENT.NEXT'})} <FaArrowRight className='ms-1 me-0' />
                      </button>
                    )}
                  </div>
                </div>
                {/*end::Actions */}
              </form>
              {/*end::Form */}
            </div>
            {/*end::Content */}
          </div>
          {/* end::Stepper */}
        </div>
      </Modal>

      {/* Confirmation modal - only shown when showConfirmation is true */}
      <Modal
        show={showConfirmation}
        onHide={() => setShowConfirmation(false)}
        centered
        backdrop='static'
      >
        <Modal.Header closeButton>
          <Modal.Title>{f({id: 'CLAIM.INPATIENT.CONFIRMATION'})}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{f({id: 'CLAIM.INPATIENT.MODAL_DAILY_MONITORING'})}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowConfirmation(false)}>
            {f({id: 'CLAIM.INPATIENT.CANCEL'})}
          </Button>
          <Button variant='primary' onClick={submit} disabled={loading}>
            {loading ? (
              <span>
                <span
                  className='spinner-border spinner-border-sm'
                  role='status'
                  aria-hidden='true'
                ></span>{' '}
                {f({id: 'CLAIM.INPATIENT.SUBMITTING'})}
              </span>
            ) : (
              f({id: 'CLAIM.INPATIENT.SUBMIT'})
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer
        position='top-right'
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme='light'
      />
    </>,
    modalsRoot
  )
}

export { CreateAppModal }